import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { redeemInviteFn } from "../lib/api";
import { mapFirebaseError } from "../lib/errors";
import { useAuth } from "./AuthContext";
import { useToast } from "../components/ToastProvider";

export const PENDING_INVITE_KEY = "pendingInviteCode";

/**
 * Watches for a signed-in user and a pendingInviteCode in sessionStorage.
 * If both are present, redeems the invite and navigates to /org/:orgId.
 *
 * This hook should be mounted exactly once at the app root.
 */
export function usePendingInviteProcessor(): void {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const inFlight = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (inFlight.current) return;

    const code = sessionStorage.getItem(PENDING_INVITE_KEY);
    if (!code) return;

    inFlight.current = true;
    redeemInviteFn({ code: code.trim().toUpperCase() })
      .then((result) => {
        sessionStorage.removeItem(PENDING_INVITE_KEY);
        toast.show(
          `Joined ${result.data.orgName} as ${result.data.role}.`,
          "success"
        );
        navigate(`/org/${result.data.orgId}`, { replace: true });
      })
      .catch((err) => {
        sessionStorage.removeItem(PENDING_INVITE_KEY);
        toast.show(mapFirebaseError(err), "error");
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [user, loading, navigate, toast]);
}
